import { formatDateTime } from '@belong/common';
import type {
  InspectionModel,
  MaintenanceModel,
  MaintenanceRecurrenceModel,
} from 'api/models';
import { LABEL_COLOR_TYPES, LabelItem } from 'components/Label/labelTypes';
import { listOfAppliances } from 'containercomponents/Modals/ModalListOfItems/ApplianceList/listOfFormObjects';
import { isAfter } from 'date-fns';
import { groupBy, startCase } from 'es-toolkit/compat';
import { STRINGS } from 'maintenance/strings';
import type { ProServiceView } from 'maintenance/types';
import type { MaintenanceQuoteConsumerModel } from 'maintenance/types/quote-centric';
import { getStringFromTime } from 'utils/formatMaintenance';
import { isEmpty } from 'utils/validation';
import { MaintenanceStatus, workOrderStatusLabel } from './status';

export const getClosedTasks = (tasks: MaintenanceModel[]) =>
  tasks?.filter((task) => task.status === 'Closed') ?? [];

export const getTurnedOffRecurringTasks = (
  tasks: MaintenanceRecurrenceModel[],
) =>
  tasks
    ?.filter((task) => task.isCancelled)
    .sort(
      (
        activeTaskA: MaintenanceRecurrenceModel,
        activeTaskB: MaintenanceRecurrenceModel,
      ) => {
        const firstDate = getRawRecurringTaskDateByStatus({
          task: activeTaskA,
        });
        const secondDate = getRawRecurringTaskDateByStatus({
          task: activeTaskB,
        });

        if (!firstDate) {
          return -1;
        }

        if (!secondDate) {
          return 1;
        }

        return isAfter(new Date(firstDate), new Date(secondDate)) ? 1 : -1;
      },
    ) ?? [];

export const getActiveTasks = (tasks: MaintenanceModel[]) =>
  tasks?.filter((task) => task.status !== 'Closed') ?? [];

export const getActiveRecurringTasks = (tasks: MaintenanceRecurrenceModel[]) =>
  tasks
    ?.filter((task) => !task.isCancelled)
    .sort(
      (
        activeTaskA: MaintenanceRecurrenceModel,
        activeTaskB: MaintenanceRecurrenceModel,
      ) => {
        const firstDate = getRawRecurringTaskDateByStatus({
          task: activeTaskA,
        });
        const secondDate = getRawRecurringTaskDateByStatus({
          task: activeTaskB,
        });

        if (!firstDate) {
          return -1;
        }

        if (!secondDate) {
          return 1;
        }

        return isAfter(new Date(firstDate), new Date(secondDate)) ? 1 : -1;
      },
    ) ?? [];

export const getTasksByUnit = (tasks: MaintenanceModel[]) =>
  groupBy(tasks, (task) => task.homeUniqueId);

export const getRecurringTasksByUnit = (tasks: MaintenanceRecurrenceModel[]) =>
  groupBy(tasks, (task) => task.homeUniqueId);

export const getTasksWithInspection = (
  tasks: MaintenanceModel[],
  inspections: InspectionModel[],
) =>
  tasks?.map((task) => ({
    ...task,
    inspection: inspections?.find((x) => x.groupUniqueId === task.uniqueId),
  }));

export const getRecurringTaskImageByRecurringTask = (
  recurringTask: MaintenanceRecurrenceModel['recurringTask'],
  // `| undefined`: the `default` branch below breaks out of the switch without
  // returning, so an unrecognised task type already produced `undefined`.
): string | undefined => {
  switch (recurringTask) {
    case 'RainSpoutAndGutterCleaning':
      return '/pro-services/create_rain_gutter.png';
    case 'Landscaping':
      return '/pro-services/create_gardening.png';
    case 'PoolMaintenance':
      return '/pro-services/create_pool_maintenance.png';
    case 'ExteriorPowerWash':
      return '/pro-services/create_exterior_power_wash.png';
    case 'ExteriorWindowWashing':
      return '/pro-services/create_exterior_washing.png';
    case 'MaintenancePackage':
      return '/pro-services/create_maintenance_package.png';
    case 'PestControl':
      return '/pro-services/create_pest_control.png';
    case 'Cleaning':
      return '/pro-services/create_cleaning.png';
    case 'MainSewerCleanOut':
      return '/pro-services/create_main_sewer.png';
    default:
      break;
  }
};

export function getFormattedFrequency(frequency: number): string {
  if (frequency / 365 === 1) {
    return 'Yearly';
  }

  if (frequency % 365 === 0) {
    const years = frequency / 365;

    return `Every ${years} Years`;
  }

  // if (frequency / 90 === 1) {
  //   return 'Quarterly';
  // }
  //
  // if (frequency % 90 === 0) {
  //   const quarters = frequency / 90;
  //
  //   return `Every ${quarters} Quarters`;
  // }

  if (frequency / 30 === 1) {
    return 'Monthly';
  }

  if (frequency % 30 === 0) {
    const months = frequency / 30;

    return `Every ${months} Months`;
  }

  if (frequency / 7 === 1) {
    return 'Weekly';
  }

  if (frequency / 14 === 1) {
    return 'Biweekly';
  }

  if (frequency % 7 === 0) {
    const weeks = frequency / 7;

    return `Every ${weeks} Weeks`;
  }

  if (frequency === 1) {
    return 'Every Day';
  }

  return `Every ${frequency} Days`;
}

type GetRecurringTaskPricingParams = {
  task: MaintenanceRecurrenceModel;
  view?: ProServiceView;
};

export function getRecurringTaskPricing({
  task,
  view = 'homeOwner',
}: GetRecurringTaskPricingParams) {
  if (view === 'homeOwner') {
    if (task.responsibility === 'Homeowner') {
      return task.estimatedCost?.totalCost ?? STRINGS['pricing-tbd'];
    }

    const responsibility = task.responsibility ?? 'Homeowner';

    return `${responsibility} ${STRINGS.responsibility}`;
  }

  if (task.responsibility === 'Resident') {
    return task.estimatedCost?.totalCost ?? STRINGS['pricing-tbd'];
  }

  return 'Homeowner Responsibility';
}

export function getRecurringTaskDateByStatus({
  task,
}: Record<'task', MaintenanceRecurrenceModel>) {
  const { recurringServiceStatus } = task;

  if (recurringServiceStatus === 'OccurringAround') {
    // The dates below are optional on the model but are set for the status each
    // branch tests, and were passed through unguarded before.
    return formatDateTime({
      dateTime: (task.requestNextOn || task.nextOn)!,
      format: 'LLLL do, Y',
    });
  }

  if (
    ['Completed', 'Cancelled', 'Scheduled'].includes(recurringServiceStatus!)
  ) {
    return formatDateTime({
      dateTime: task.appointmentNextOn!,
      format: 'LLLL do, Y',
    });
  }

  if (recurringServiceStatus === 'TurnedOff') {
    return formatDateTime({
      dateTime: task.cancelledOn!,
      format: 'LLLL do, Y',
    });
  }

  return '';
}

function getRawRecurringTaskDateByStatus({
  task,
}: Record<'task', MaintenanceRecurrenceModel>) {
  const { recurringServiceStatus } = task;

  if (recurringServiceStatus === 'OccurringAround') {
    return task.requestNextOn;
  }

  if (
    ['Completed', 'Cancelled', 'Scheduled'].includes(recurringServiceStatus!)
  ) {
    return task.appointmentNextOn;
  }

  if (recurringServiceStatus === 'TurnedOff') {
    return task.cancelledOn;
  }

  return '';
}

export function getRecurringTaskStatus({
  task,
}: Record<'task', MaintenanceRecurrenceModel>) {
  if (task.recurringServiceStatus === 'OccurringAround') {
    return STRINGS['estimated-date'];
  }

  return startCase(task.recurringServiceStatus);
}

export type Task = {
  label: string;
  image: string;
  key?: string;
  recurring?: boolean;
  multiUnit?: boolean;
};

export const baseTasks: Task[] = [
  {
    label: 'Repair Something',
    image: '/pro-services/create_repair_something.png',
    multiUnit: true,
    key: 'Repair',
  },
  {
    label: 'Request Upgrade',
    image: '/pro-services/create_upgrade.png',
    multiUnit: true,
    key: 'Upgrade',
  },
];

export const recurringTasksHomeowner: Task[] = [
  {
    label: 'Landscaping',
    image: '/pro-services/create_gardening.png',
    key: 'Landscaping',
    recurring: true,
  },
  {
    label: 'Interior\nCleaning',
    image: '/pro-services/create_cleaning.png',
    key: 'Cleaning',
    recurring: true,
  },
  {
    label: 'Exterior Power\nWashing',
    image: '/pro-services/create_exterior_power_wash.png',
    key: 'ExteriorPowerWash',
    recurring: true,
  },
  {
    label: 'Exterior Window\nWashing',
    image: '/pro-services/create_exterior_washing.png',
    key: 'ExteriorWindowWashing',
    recurring: true,
  },
  {
    label: 'Pool\nMaintenance',
    image: '/pro-services/create_pool_maintenance.png',
    key: 'PoolMaintenance',
    recurring: true,
  },
  {
    label: 'Maintenance\nPackage',
    image: '/pro-services/create_maintenance_package.png',
    key: 'MaintenancePackage',
    recurring: true,
    multiUnit: true,
  },
  {
    label: 'Gutter\nCleaning',
    image: '/pro-services/create_rain_gutter.png',
    key: 'RainSpoutAndGutterCleaning',
    recurring: true,
  },
  {
    label: 'Pest Control',
    image: '/pro-services/create_pest_control.png',
    key: 'PestControl',
    recurring: true,
  },
  {
    label: 'Main Sewer\nClean\u2011out',
    image: '/pro-services/create_main_sewer.png',
    key: 'MainSewerCleanOut',
    recurring: true,
  },
];

export const residentTasks: Task[] = [
  {
    label: 'Landscaping',
    image: '/pro-services/create_gardening.png',
    key: 'Landscaping',
    recurring: true,
  },
  {
    label: 'Interior\nCleaning',
    image: '/pro-services/create_cleaning.png',
    key: 'Cleaning',
    recurring: true,
  },
  {
    label: 'Pool\nMaintenance',
    image: '/pro-services/create_pool_maintenance.png',
    key: 'PoolMaintenance',
    recurring: true,
  },
  {
    label: 'Pest Control',
    image: '/pro-services/create_pest_control.png',
    key: 'PestControl',
    recurring: true,
  },
];

export const upgradesNames = {
  mounttv: { label: 'Mount TV', approval: false },
  interiorpainting: { label: 'Interior Painting', approval: true },
  windowac: { label: 'Get a Window AC', approval: true },
  hvacfilter: { label: 'HVAC Filter Replacement', approval: false },
  ringdoorbell: { label: 'Ring Doorbell', approval: true },
  teslacharger: { label: 'Tesla or EV Charger', approval: true },
  nestthermostat: { label: 'Nest Thermostat', approval: true },
  carpetcleaning: { label: 'Carpet Cleaning', approval: true },
};

const shouldRequestResponsibilityConsent = (
  task: MaintenanceModel,
  isRenter: boolean,
) => {
  if (task.actualCost) {
    return false;
  }
  if (task.requestCategory !== 'InHomeRepairs' && !task.estimatedCost) {
    return false;
  }
  if (!task.payments?.length) {
    return false;
  }
  const renterResponsibility = task.payments?.find(
    (payment) => payment.paidBy === 'Resident',
  );
  const homeOwnerResponsibility = task.payments?.find(
    (payment) => payment.paidBy === 'Homeowner',
  );
  if (renterResponsibility?.consentStatus === 'Required' && isRenter) {
    return true;
  } else if (
    homeOwnerResponsibility?.consentStatus === 'Required' &&
    !isRenter
  ) {
    return true;
  }
  return false;
};

export const isPendingHomeownerApproval = (task: MaintenanceModel) =>
  task.pendingConsent || shouldRequestResponsibilityConsent(task, false);

export const getMaintenanceLabels = (
  task: MaintenanceModel,
  approvalRequired: boolean,
): LabelItem[] => {
  const labels: LabelItem[] = [];

  if (!task.uniqueId) {
    return [];
  }

  if (task.urgency === 'Urgent') {
    labels.push({
      text: MaintenanceStatus.Urgent,
      color: LABEL_COLOR_TYPES.RED,
    });
  }

  if ('recurrence' in task && task.recurrence?.repeatFrequencyDays) {
    labels.push({
      // Non-null: the `if` above already required a truthy frequency, and
      // `getStringFromTime` returns `undefined` only for a non-positive one.
      text: getStringFromTime(task.recurrence.repeatFrequencyDays)!,
    });
  }

  if (task.status === 'Closed') {
    labels.push({
      text: MaintenanceStatus.Closed,
      color: LABEL_COLOR_TYPES.WHITE,
    });

    return labels;
  }

  if (task.result === 'Cancelled') {
    labels.push({
      text: MaintenanceStatus.Cancelled,
      color: LABEL_COLOR_TYPES.GRAY_BORDER,
    });

    return labels;
  }

  if (task.result === 'Completed') {
    labels.push({
      text: MaintenanceStatus.Completed,
      color: LABEL_COLOR_TYPES.GREEN,
    });

    return labels;
  }

  if (approvalRequired) {
    labels.push({
      text: MaintenanceStatus.WaitingForQuoteApproval,
      color: LABEL_COLOR_TYPES.RED_BORDER,
    });
  } else {
    if (task.workOrder?.status) {
      labels.push({
        text: MaintenanceStatus[task.workOrder.status],
        color: workOrderStatusLabel[task.workOrder.status],
      });
    }

    if (task.requestCategory !== 'InHomeRepairs') {
      if (task.nextAppointmentUniqueId) {
        labels.push({
          text: MaintenanceStatus.AppointmentScheduled,
          color: LABEL_COLOR_TYPES.BLUE,
        });
        return labels;
      }

      if (!task.nextAppointmentUniqueId && !!task.appointments.length) {
        labels.push({
          text: 'Followup Required',
          color: LABEL_COLOR_TYPES.NAVY,
        });
        return labels;
      }
    }
  }

  // `task.result` can be one of the seven results `MaintenanceStatus` has no
  // entry for, so this lookup genuinely misses — as it already did, silently.
  // The `if` below is the existing guard, kept verbatim.
  const resultLabel = task.result
    ? (MaintenanceStatus as Record<string, string | undefined>)[task.result]
    : undefined;

  if (resultLabel) {
    labels.push({
      text: resultLabel,
      color: LABEL_COLOR_TYPES.DARK_GRAY,
    });
  }

  if (task.status === 'InProgress') {
    labels.push({
      text: MaintenanceStatus.InProgress,
      color: LABEL_COLOR_TYPES.BLUE,
    });
  }

  return labels;
};

export const validateMaintenanceSummary = (summary: string) => {
  if (!listOfAppliances.some((appliance) => appliance.value === summary)) {
    return 'Select a valid appliance from the list';
  }

  if (isEmpty(summary)) {
    return 'Required';
  }
};

export const isQuoteApproved = (q: MaintenanceQuoteConsumerModel) =>
  q.status === 'Approved';

export const isQuoteRejected = (q: MaintenanceQuoteConsumerModel) =>
  q.status === 'Rejected' ||
  q.status === 'NotSelected' ||
  q.approvals.some((a) => a.consentStatus === 'Rejected');
